import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { AddOnSlice } from "./slices/createAddOnSlice";
import type { ChessboardThemeSlice } from "./slices/createChessboardThemeSlice";
import type { NotificationSlice } from "./slices/createNotificationSlice";
import type { OrderDataSlice } from "./slices/createOrderDataSlice";
import type { PaymentSlice } from "./slices/createPaymentSlice";
import type { PersonalInfoSlice } from "./slices/createPersonalInfoSlice";
import type { PlanSlice } from "./slices/createPlanSlice";
import type { ProductSlice } from "./slices/createProductSlice";
import type { PuzzleStateSlice } from "./slices/createPuzzleStateSlice";
import type { RushStateSlice } from "./slices/createRushStateSlice";
import type { StepSlice } from "./slices/createStepSlice";
import type { SubmitFormSlice } from "./slices/createSubmitFormSlice";
import type { ToggleSubcriptionPlanSlice } from "./slices/createToggleSubscriptionPlanSlice";

import {
    createAddOnSlice,
    createChessboardThemeSlice,
    createNotificationSlice,
    createOrderDataSlice,
    createPaymentSlice,
    createPersonalInfoSlice,
    createPlanSlice,
    createProductSlice,
    createPuzzleStateSlice,
    createRushStateSlice,
    createStepSlice,
    createSubmitFormSlice,
    createToggleSubscriptionPlanSlice,
} from "./slices";
import { createTtlStorage } from "./ttl-storage";

export type StoreState = PersonalInfoSlice &
    PlanSlice &
    AddOnSlice &
    ToggleSubcriptionPlanSlice &
    StepSlice &
    SubmitFormSlice &
    PaymentSlice &
    ProductSlice &
    OrderDataSlice &
    ChessboardThemeSlice &
    PuzzleStateSlice &
    RushStateSlice &
    NotificationSlice;

/** Never written to storage: session-only or sensitive. */
const UNPERSISTED_KEYS = new Set<keyof StoreState>([
    "puzzleState",
    "rushState",
    "payment",
    "classInvites",
    "gameInvites",
    "battleInvites",
]);

const CARD_FIELDS = ["cardname", "cardnumber", "expire", "securitycode"];

const useStore = create<StoreState>()(
    persist(
        (...a) => ({
            ...createPersonalInfoSlice(...a),
            ...createPlanSlice(...a),
            ...createAddOnSlice(...a),
            ...createToggleSubscriptionPlanSlice(...a),
            ...createStepSlice(...a),
            ...createSubmitFormSlice(...a),
            ...createPaymentSlice(...a),
            ...createProductSlice(...a),
            ...createOrderDataSlice(...a),
            ...createChessboardThemeSlice(...a),
            ...createPuzzleStateSlice(...a),
            ...createRushStateSlice(...a),
            ...createNotificationSlice(...a),
        }),
        {
            name: "use-store",
            storage: createTtlStorage({
                ttlMs: 30 * 60 * 1000,
                preserveKeys: ["chessboardTheme"],
            }),
            partialize: (state) =>
                Object.fromEntries(
                    Object.entries(state).filter(
                        ([key]) =>
                            !UNPERSISTED_KEYS.has(key as keyof StoreState),
                    ),
                ),
            // Bump when the persisted shape changes. Zustand writes the migrated
            // state back to storage right after hydration, so a migration is the
            // only way to guarantee a rewrite without waiting for a state change.
            // v1: drop the card fields that were persisted before #616.
            version: 1,
            migrate: (persistedState) => {
                const state = {
                    ...(persistedState as Record<string, unknown>),
                };
                delete state.payment;
                if (state.orderData && typeof state.orderData === "object") {
                    const orderData = {
                        ...(state.orderData as Record<string, unknown>),
                    };
                    for (const key of CARD_FIELDS) delete orderData[key];
                    state.orderData = orderData;
                }
                return state;
            },
        },
    ),
);

export default useStore;
